import {
  type FC,
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';

import { useExchanges } from '@/hooks/useExchanges';
import type { Exchange } from '@/types';

type ExchangeInfo = Omit<Exchange, 'name'>;

type ExchangesContextType = {
  exchanges: Exchange[] | undefined;
  exchangesMap: Map<string, ExchangeInfo>;
  isLoading: boolean;
  error: Error | null;
};

const ExchangesContext = createContext<ExchangesContextType | undefined>(
  undefined
);

export const useExchangesContext = () => {
  const context = useContext(ExchangesContext);
  if (!context) {
    throw new Error(
      'useExchangesContext must be used within an ExchangesProvider'
    );
  }
  return context;
};

const ExchangesProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data: exchanges, isLoading, error } = useExchanges();

  const exchangesMap = useMemo(() => {
    const map = new Map<string, ExchangeInfo>();
    if (exchanges) {
      exchanges.forEach(({ name, ...exchange }) => {
        map.set(name, exchange);
      });
    }
    return map;
  }, [exchanges]);

  return (
    <ExchangesContext.Provider
      value={{ exchanges, exchangesMap, isLoading, error }}
    >
      {children}
    </ExchangesContext.Provider>
  );
};

export default ExchangesProvider;
